/**
 * Le scan des étiquettes QR au pointage de la garderie : ce que le téléphone sait sans la base
 * (conception : `docs/garderie/SCAN-QR.md` ; migration `20261103200000_le_scan_des_etiquettes`).
 *
 * Pur, sans DOM : la lecture stricte de l'étiquette (§ 1), la plage à l'heure du scan (§ 2.1,
 * table de cas § 2.3 dans les tests), l'écart d'horloge au serveur, la file des scans et ses
 * lots (§ 3.3, § 5.2), l'anti-rebond (§ 3.1, 4), les textes des retours.
 *
 * Au lot 1, c'est le téléphone qui calcule la plage ; la base vérifie seulement qu'elle est de
 * l'école, active, et qu'elle tourne ce jour-là (et calcule elle-même « parti avant l'appel »).
 * Au lot 3, la règle deviendra une fonction SQL dont ce fichier sera la copie vérifiée sur la
 * même table. Un scan pose « présent » et ne décoche jamais (VALIDÉ ÉCLOSION, 03/11/2026).
 */
import { jourDeLaSemaine } from './calcul';
import type { PlageDuLien } from './pointage';

export type ModeDeScan = 'appel' | 'sortie';
export type Saisie = 'qr' | 'recherche';
export type ResultatDeScan =
  | 'pointe'
  | 'deja_pointe'
  | 'inconnu'
  | 'sorti'
  | 'a_verifier'
  | 'pas_de_garderie'
  | 'parti_avant_l_appel'
  | 'retiree_depuis'
  | 'mois_facture'
  | 'annule'
  | 'garde'
  | 'rien_a_annuler'
  | 'invalide'
  | 'non_traite';

const RESULTATS: readonly ResultatDeScan[] = [
  'pointe', 'deja_pointe', 'inconnu', 'sorti', 'a_verifier', 'pas_de_garderie', 'parti_avant_l_appel',
  'retiree_depuis', 'mois_facture', 'annule', 'garde', 'rien_a_annuler', 'invalide', 'non_traite',
];

/* ------------------------------------------------------------------------- */
/* L'étiquette                                                                */
/* ------------------------------------------------------------------------- */

/**
 * Le numéro QuickSchool d'une étiquette (`IDEleve=1404`), lu strictement, comme la base
 * (`^\s*IDEleve=0*([1-9][0-9]{0,6})\s*$`, casse ignorée) : `'IDEleve=01404'` → `'1404'`.
 * Tout autre contenu (adresse web, QR de virement, numéro vide ou trop long) → `null` :
 * « Pas une étiquette de garderie », rien n'entre dans la file. Le texte n'est jamais traité
 * comme une adresse ni comme du HTML.
 */
export function lireEtiquette(texte: string): string | null {
  if (typeof texte !== 'string' || texte.length > 64) return null;
  const m = /^\s*IDEleve=0*([1-9][0-9]{0,6})\s*$/i.exec(texte);
  return m ? m[1] : null;
}

/**
 * Le numéro tapé à la main depuis l'étiquette, qui l'imprime avec le séparateur belge des
 * milliers : `'1.404'`, `'1 404'` ou `'1404'` → `'1404'` ; autre chose → `null`.
 */
export function numeroSaisi(texte: string): string | null {
  const t = (texte ?? '').trim();
  const groupe = /^\d{1,3}([. \u00a0\u202f]\d{3})+$/;
  if (!/^\d+$/.test(t) && !groupe.test(t)) return null;
  const m = /^0*([1-9][0-9]{0,6})$/.exec(t.replace(/[. \u00a0\u202f]/g, ''));
  return m ? m[1] : null;
}

/* ------------------------------------------------------------------------- */
/* La plage à l'heure du scan                                                 */
/* ------------------------------------------------------------------------- */

export type PlageDuScan =
  | { plage: PlageDuLien; apresLaFin: boolean }
  | { aucune: 'pas_de_garderie' | 'a_verifier' | 'parti_avant_l_appel' };

/** `HH:MM` ou `HH:MM:SS` → secondes depuis minuit ; `NaN` si illisible. */
export function enSecondes(heure: string): number {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(heure ?? '');
  if (!m) return Number.NaN;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] ?? 0);
}

interface Horaire {
  plage: PlageDuLien;
  debut: number;
  fin: number;
}

/** Les plages qui tournent ce jour-là, avec l'horaire du jour s'il y en a un, triées par début. */
function horairesDuJour(plages: readonly PlageDuLien[], date: string): Horaire[] {
  const jour = jourDeLaSemaine(date);
  return plages
    .filter((p) => p.jours.includes(jour))
    .map((p) => {
      const propre = p.horairesDuJour?.[jour];
      return { plage: p, debut: enSecondes(propre?.debut ?? p.heureDebut), fin: enSecondes(propre?.fin ?? p.heureFin) };
    })
    .filter((h) => Number.isFinite(h.debut) && Number.isFinite(h.fin))
    .sort((a, b) => a.debut - b.debut || a.fin - b.fin);
}

/**
 * La plage d'un scan fait le `date` à `heure` (heure de Bruxelles, déjà corrigée de l'écart de
 * l'horloge du téléphone), parmi les plages actives de l'école (SCAN-QR § 2.1) :
 *
 * 0. aucune ne tourne ce jour-là, ou jour férié → `pas_de_garderie` ;
 * 1. mode « Sortie » et une plage du jour a une heure d'appel (`ATL1` d'ISA, 15:45) encore à
 *    venir → `parti_avant_l_appel` (VALIDÉ ÉCLOSION, 03/11/2026) ;
 * 2. une plage en cours (`début ≤ t < fin` ; la dernière commencée) : retenue, sauf si elle en
 *    implique une autre et que `t < début + tolérance` → la plage impliquée (16:00–16:05 : ATL1) ;
 * 3. sinon la prochaine plage du jour, si `t ≥ début − avance` ;
 * 4. sinon une plage terminée depuis moins de `retard` minutes (la plus récemment terminée), ou
 *    la dernière plage du jour après sa fin, jusqu'à minuit : « après la fin » ;
 * 5. sinon → `a_verifier`.
 *
 * Le mode ne change la plage que par la règle 1 ; `null` (la liste) l'ignore.
 */
export function plageDuScan(plages: readonly PlageDuLien[], date: string, heure: string, mode: ModeDeScan | null, ferie: boolean): PlageDuScan {
  const duJour = horairesDuJour(plages, date);
  if (ferie || duJour.length === 0) return { aucune: 'pas_de_garderie' };
  const t = enSecondes(heure);
  if (!Number.isFinite(t)) return { aucune: 'a_verifier' };

  // 1. La sortie avant l'appel.
  if (mode === 'sortie' && duJour.some((h) => h.plage.appelA !== null && t < enSecondes(h.plage.appelA))) {
    return { aucune: 'parti_avant_l_appel' };
  }

  // 2. Une plage en cours : la dernière commencée.
  const enCours = duJour.filter((h) => h.debut <= t && t < h.fin);
  if (enCours.length > 0) {
    const h = enCours[enCours.length - 1];
    if (h.plage.implique && t < h.debut + h.plage.toleranceMin * 60) {
      const impliquee = duJour.find((x) => x.plage.id === h.plage.implique);
      if (impliquee) return { plage: impliquee.plage, apresLaFin: false };
    }
    return { plage: h.plage, apresLaFin: false };
  }

  // 3. La prochaine, avec son avance.
  const prochaine = duJour.find((h) => h.debut > t);
  if (prochaine && t >= prochaine.debut - prochaine.plage.avanceMin * 60) return { plage: prochaine.plage, apresLaFin: false };

  // 4. Une plage à peine finie, ou la dernière du jour après sa fin.
  const finies = duJour.filter((h) => h.fin <= t).sort((a, b) => b.fin - a.fin || b.debut - a.debut);
  const recente = finies.find((h) => t < h.fin + h.plage.retardMin * 60);
  if (recente) return { plage: recente.plage, apresLaFin: true };
  const derniere = [...duJour].sort((a, b) => b.fin - a.fin || b.debut - a.debut)[0];
  if (t >= derniere.fin) return { plage: derniere.plage, apresLaFin: true };

  // 5. À vérifier.
  return { aucune: 'a_verifier' };
}

/**
 * Quand la règle ne donne aucune plage : la prochaine plage du jour à commencer après `heure`,
 * sinon la dernière du jour (`null` s'il n'y en a aucune). Sert à la liste (`plageParDefaut`).
 */
export function prochaineOuDerniere(plages: readonly PlageDuLien[], date: string, heure: string): PlageDuLien | null {
  const duJour = horairesDuJour(plages, date);
  if (duJour.length === 0) return null;
  const t = enSecondes(heure);
  const prochaine = Number.isFinite(t) ? duJour.find((h) => h.debut > t) : undefined;
  return (prochaine ?? [...duJour].sort((a, b) => b.fin - a.fin || b.debut - a.debut)[0]).plage;
}

/* ------------------------------------------------------------------------- */
/* L'heure du scan                                                            */
/* ------------------------------------------------------------------------- */

/**
 * Un instant rendu par la base (`timestamptz` en JSON : `2026-11-03T15:46:12.123456+00:00`), en
 * ms epoch ; `NaN` s'il est illisible. Les fractions au-delà de la milliseconde sont coupées
 * avant la lecture : tous les navigateurs ne lisent pas six décimales (HYPOTHÈSE À VÉRIFIER sur
 * Safari ; la coupe ne coûte rien).
 */
export function instantDeLaBase(iso: string | null | undefined): number {
  if (typeof iso !== 'string' || iso === '') return Number.NaN;
  return Date.parse(iso.replace(/(\.\d{3})\d+/, '$1'));
}

/**
 * L'écart (ms) entre l'horloge du serveur et celle du téléphone, mesuré sur une lecture :
 * l'heure du serveur moins le milieu de l'aller-retour (`envoyeLe`, `recuLe` : `Date.now()` du
 * téléphone). Positif quand le téléphone retarde. `0` si l'heure du serveur est illisible.
 */
export function ecartAuServeur(maintenantServeur: string, envoyeLe: number, recuLe: number): number {
  const serveur = instantDeLaBase(maintenantServeur);
  if (!Number.isFinite(serveur) || !Number.isFinite(envoyeLe) || !Number.isFinite(recuLe)) return 0;
  return Math.round(serveur - (envoyeLe + recuLe) / 2);
}

/** Le moment du scan à l'horloge du serveur (ms epoch). */
export function momentCorrige(momentAppareil: number, ecartMs: number): number {
  return momentAppareil + (Number.isFinite(ecartMs) ? ecartMs : 0);
}

const FORMAT_BRUXELLES = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Brussels',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** La date (`AAAA-MM-JJ`) et l'heure (`HH:MM:SS`) à Bruxelles d'un moment (ms epoch). */
export function dateEtHeureBruxelles(moment: number): { date: string; heure: string } {
  const parties = FORMAT_BRUXELLES.formatToParts(new Date(moment));
  const v = (type: string) => parties.find((p) => p.type === type)?.value ?? '00';
  return { date: `${v('year')}-${v('month')}-${v('day')}`, heure: `${v('hour')}:${v('minute')}:${v('second')}` };
}

/* ------------------------------------------------------------------------- */
/* La file et les lots                                                        */
/* ------------------------------------------------------------------------- */

/**
 * Un scan dans la file du téléphone, écrit avant tout envoi (SCAN-QR § 3.3). **Aucun nom
 * d'enfant** : le QR lu ou l'identifiant de l'enfant (« Chercher »). `source` : le jeton du lien
 * ou `bureau-<école>` ; la file d'une source part par cette source — ou, pour un lien renouvelé,
 * par le nouveau lien de la même école (`repartirLesAutresSources`). `ecole` : l'école de la
 * source (`center_id`, pas une donnée d'enfant) ; `null` pour une entrée écrite sans elle.
 */
export interface EntreeDeFile {
  cle: string;
  source: string;
  ecole: string | null;
  mode: ModeDeScan | 'annulation';
  saisie: Saisie | null;
  qr: string | null;
  enfantId: string | null;
  annuleCle: string | null;
  plageId: string | null;
  /** ISO. */
  momentAppareil: string;
  ecartMs: number;
  pointePar: string | null;
  tentatives: number;
  /** ms epoch. */
  prochainEssai: number;
}

export interface ResultatDuScan {
  cle: string | null;
  resultat: ResultatDeScan;
  definitif: boolean;
  enfant: { id: string; prenom: string; initiale: string; classe: string | null } | null;
  plageId: string | null;
  plageCode: string | null;
  date: string | null;
  deja: { par: string | null; le: string } | null;
}

export type ReponseDuScan =
  | { codeRequis: true; maintenant: string | null }
  | { codeRequis: false; maintenant: string | null; resultats: ResultatDuScan[] };

type J = Record<string, unknown>;
const o = (v: unknown): J => (v && typeof v === 'object' && !Array.isArray(v) ? (v as J) : {});
const s = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/**
 * La réponse de `garderie_lien_scanner` / `garderie_scanner`. Un résultat inconnu du téléphone
 * se lit `invalide` ; un résultat sans `definitif: true` reste non définitif (le scan reste en
 * file) ; une réponse illisible ne rend aucun résultat (tout reste en file).
 */
export function lireReponseDuScan(data: unknown): ReponseDuScan {
  const d = o(data);
  const maintenant = s(d.maintenant);
  if (d.code_requis === true) return { codeRequis: true, maintenant };
  const resultats = (Array.isArray(d.resultats) ? d.resultats : []).map((r): ResultatDuScan => {
    const x = o(r);
    const brut = s(x.resultat);
    const resultat = brut && (RESULTATS as readonly string[]).includes(brut) ? (brut as ResultatDeScan) : 'invalide';
    const e = x.enfant && typeof x.enfant === 'object' ? o(x.enfant) : null;
    const deja = x.deja && typeof x.deja === 'object' ? o(x.deja) : null;
    return {
      cle: s(x.cle),
      resultat,
      definitif: x.definitif === true && resultat !== 'non_traite',
      enfant: e ? { id: s(e.id) ?? '', prenom: s(e.prenom) ?? '', initiale: s(e.initiale) ?? '', classe: s(e.classe) } : null,
      plageId: s(x.plage_id),
      plageCode: s(x.plage_code),
      date: s(x.date),
      deja: deja ? { par: s(deja.par), le: s(deja.le) ?? '' } : null,
    };
  });
  return { codeRequis: false, maintenant, resultats };
}

/** Une entrée de la file, dans la forme de l'entrée de la fonction de la base (contrat § A.6). */
export function entreeEnJson(e: EntreeDeFile): Record<string, unknown> {
  return {
    cle: e.cle,
    mode: e.mode,
    saisie: e.saisie,
    qr: e.qr,
    enfant_id: e.enfantId,
    annule_cle: e.annuleCle,
    plage_id: e.plageId,
    moment_appareil: e.momentAppareil,
    ecart_ms: Math.round(e.ecartMs),
    pointe_par: e.pointePar,
  };
}

/** Au plus 25 scans par lot : la base refuse au-delà (`trop_de_scans`). */
export const SCANS_PAR_LOT = 25;

const instant = (e: EntreeDeFile): number => {
  const t = Date.parse(e.momentAppareil);
  return Number.isFinite(t) ? t : 0;
};

/** Les plus anciennes d'abord (moment du scan, puis clé) : l'ordre d'envoi. */
export function ordreDeLaFile(file: readonly EntreeDeFile[]): EntreeDeFile[] {
  return [...file].sort((a, b) => instant(a) - instant(b) || (a.cle < b.cle ? -1 : a.cle > b.cle ? 1 : 0));
}

/**
 * Le lot à envoyer : les plus anciennes entrées dont l'essai est dû (`prochainEssai ≤
 * maintenant`), 25 au plus, toutes de la source de la plus ancienne. Une annulation ne part pas
 * avant le scan qu'elle annule quand celui-ci attend encore dans la file (sinon la base
 * répondrait « rien à annuler », définitivement, puis pointerait le scan).
 */
export function lotAEnvoyer(file: readonly EntreeDeFile[], maintenant: number, max: number = SCANS_PAR_LOT): EntreeDeFile[] {
  const limite = Math.max(0, Math.min(max, SCANS_PAR_LOT));
  const dues = ordreDeLaFile(file).filter((e) => e.prochainEssai <= maintenant);
  if (dues.length === 0 || limite === 0) return [];
  const source = dues[0].source;
  const enFile = new Set(file.map((e) => e.cle));
  const pris = new Set<string>();
  const lot: EntreeDeFile[] = [];
  for (const e of dues) {
    if (lot.length >= limite) break;
    if (e.source !== source) continue;
    if (e.mode === 'annulation' && e.annuleCle && enFile.has(e.annuleCle) && !pris.has(e.annuleCle)) continue;
    lot.push(e);
    pris.add(e.cle);
  }
  return lot;
}

/** Vrai pour la source de Gestion (`bureau-<école>`), faux pour le jeton d'un lien. */
export function estSourceDeGestion(source: string): boolean {
  return source.startsWith('bureau-');
}

/**
 * Les scans qui attendent sur le téléphone sous une **autre** source que celle ouverte
 * (`toutes` : toute la file, toutes sources). Un lien se renouvelle (VALIDÉ ÉCLOSION,
 * 03/11/2026 : « je changerai de temps en temps le lien ») et l'ancien est alors révoqué : ses
 * scans restés sur le téléphone ne partiraient plus jamais. Ouvert sur un lien vivant de la
 * même école, le téléphone les **reprend** (`aReprendre` : la source réécrite, l'essai dû
 * aussitôt) ; la base rend le résultat enregistré d'une clé déjà reçue par l'ancien lien.
 * Le reste — une autre école, Gestion, une entrée sans école — est **orphelin** : gardé, jamais
 * effacé, et montré à l'écran. PROPOSITION TECHNIQUE (la reprise sans geste explicite : À
 * DÉCIDER MÉTIER ; elle ne donne pas plus de pouvoir que le nouveau lien et son code).
 */
export function repartirLesAutresSources(
  toutes: readonly EntreeDeFile[],
  source: string,
  ecole: string | null,
): { aReprendre: EntreeDeFile[]; orphelines: EntreeDeFile[] } {
  const aReprendre: EntreeDeFile[] = [];
  const orphelines: EntreeDeFile[] = [];
  for (const e of ordreDeLaFile(toutes)) {
    if (e.source === source) continue;
    const reprenable = !!ecole && e.ecole === ecole && !estSourceDeGestion(source) && !estSourceDeGestion(e.source);
    if (reprenable) aReprendre.push({ ...e, source, prochainEssai: 0 });
    else orphelines.push(e);
  }
  return { aReprendre, orphelines };
}

/** Le délai avant un nouvel essai après `tentatives` échecs : 2 s, 4 s, 8 s… 60 s au plus. */
export function delaiApres(tentatives: number): number {
  const n = Math.max(1, Math.floor(Number.isFinite(tentatives) ? tentatives : 1));
  return Math.min(60_000, 2_000 * 2 ** Math.min(n - 1, 10));
}

/**
 * La file après la réponse de la base à un lot (`envoyees`). Un résultat définitif retire le
 * scan de la file et le rend dans `traites` ; un résultat non définitif (`non_traite`), une
 * clé sans résultat, « code requis » ou un échec (réseau, erreur) le laissent en file, avec une
 * tentative de plus et un prochain essai décalé (`delaiApres`). Les entrées qui n'étaient pas
 * du lot ne bougent pas. Les résultats se rattachent par clé, ou par position quand la base ne
 * rend pas de clé (entrée mal formée).
 */
export function apresReponse(
  file: readonly EntreeDeFile[],
  envoyees: readonly EntreeDeFile[],
  reponse: ReponseDuScan | 'echec',
  maintenant: number,
): { file: EntreeDeFile[]; traites: ResultatDuScan[] } {
  const resultats = reponse !== 'echec' && !reponse.codeRequis ? reponse.resultats : [];
  const parCle = new Map<string, ResultatDuScan>();
  for (const r of resultats) if (r.cle) parCle.set(r.cle, r);
  const definitifs = new Map<string, ResultatDuScan>();
  envoyees.forEach((e, i) => {
    const r = parCle.get(e.cle) ?? (resultats[i] && resultats[i].cle === null ? resultats[i] : undefined);
    if (r && r.definitif) definitifs.set(e.cle, { ...r, cle: e.cle });
  });
  const envoyeesCles = new Set(envoyees.map((e) => e.cle));
  const nouvelle: EntreeDeFile[] = [];
  const traites: ResultatDuScan[] = [];
  for (const e of file) {
    const r = definitifs.get(e.cle);
    if (r) {
      traites.push(r);
      continue;
    }
    if (envoyeesCles.has(e.cle)) {
      const tentatives = e.tentatives + 1;
      nouvelle.push({ ...e, tentatives, prochainEssai: maintenant + delaiApres(tentatives) });
    } else {
      nouvelle.push(e);
    }
  }
  return { file: nouvelle, traites };
}

/* ------------------------------------------------------------------------- */
/* L'anti-rebond                                                              */
/* ------------------------------------------------------------------------- */

/**
 * Le même QR est ignoré tant qu'il reste devant la caméra, puis 4 s après sa dernière lecture
 * (SCAN-QR § 3.1, 4) : vingt lectures du même QR en 3 s donnent un seul scan. Deux QR
 * différents passent l'un après l'autre.
 */
export class AntiRebond {
  private readonly delaiMs: number;
  private readonly vus = new Map<string, number>();

  constructor(delaiMs = 4000) {
    this.delaiMs = delaiMs;
  }

  accepte(contenu: string, maintenant: number): boolean {
    for (const [c, t] of this.vus) if (maintenant - t >= this.delaiMs) this.vus.delete(c);
    const dernier = this.vus.get(contenu);
    this.vus.set(contenu, maintenant);
    return dernier === undefined;
  }
}

/* ------------------------------------------------------------------------- */
/* Les textes                                                                 */
/* ------------------------------------------------------------------------- */

export const PAS_UNE_ETIQUETTE = 'Pas une étiquette de garderie';

/** `HH:MM` à Bruxelles d'un moment ISO, en « 15h46 », ou `''`. */
export function heureEnClair(iso: string | null): string {
  const t = instantDeLaBase(iso);
  if (!Number.isFinite(t)) return '';
  const { heure } = dateEtHeureBruxelles(t);
  return `${heure.slice(0, 2)}h${heure.slice(3, 5)}`;
}

/** Ce qu'un scan donne en ce qui compte pour le compteur : vert, en attente ou à revoir. */
export function estARevoir(r: Pick<ResultatDuScan, 'resultat' | 'definitif'>): boolean {
  return r.definitif && !['pointe', 'deja_pointe', 'annule', 'garde', 'rien_a_annuler'].includes(r.resultat);
}

/**
 * Le retour d'un scan dans la liste des derniers scans (SCAN-QR § 3.1, 5) : vert pour un scan
 * pointé (ou annulé), orange pour « déjà pointé », « à vérifier » et ce qui ne change rien,
 * rouge pour un refus, gris tant que la base n'a pas tranché.
 */
export function libelleDuResultat(r: ResultatDuScan, plages: readonly PlageDuLien[]): { ton: 'vert' | 'orange' | 'rouge' | 'gris'; texte: string } {
  const plage = r.plageCode ?? plages.find((p) => p.id === r.plageId)?.code ?? null;
  const qui = r.enfant ? `${r.enfant.prenom}${r.enfant.initiale ? ` ${r.enfant.initiale}.` : ''}` : null;
  const nom = qui ?? 'Enfant';
  const morceaux = (...m: (string | null)[]) => m.filter((x): x is string => !!x).join(' · ');
  switch (r.resultat) {
    case 'pointe':
      return { ton: 'vert', texte: morceaux(nom, r.enfant?.classe ?? null, plage) };
    case 'deja_pointe': {
      const h = r.deja ? heureEnClair(r.deja.le) : '';
      const par = r.deja?.par ? ` par ${r.deja.par}` : '';
      return { ton: 'orange', texte: morceaux(nom, `déjà pointé${plage ? ` ${plage}` : ''}${h ? ` à ${h}` : ''}${par}`) };
    }
    case 'inconnu':
      return { ton: 'rouge', texte: 'Étiquette inconnue dans cette école : passez par « Chercher »' };
    case 'sorti':
      return { ton: 'rouge', texte: morceaux(nom, 'ne fréquente plus la garderie : rien n’est pointé') };
    case 'a_verifier':
      return { ton: 'orange', texte: morceaux(qui, 'à vérifier (heure hors plage ou horloge douteuse) : rien n’est pointé') };
    case 'pas_de_garderie':
      return { ton: 'rouge', texte: morceaux(qui, 'pas de garderie ce jour-là : rien n’est pointé') };
    case 'parti_avant_l_appel': {
      const appel = plages.find((p) => p.appelA)?.appelA ?? null;
      const de = appel ? ` de ${appel.slice(0, 2)}h${appel.slice(3, 5)}` : '';
      return { ton: 'rouge', texte: morceaux(qui, `parti avant l’appel${de} : rien n’est pointé`) };
    }
    case 'retiree_depuis':
      return { ton: 'rouge', texte: morceaux(nom, `présence${plage ? ` ${plage}` : ''} retirée depuis par une personne : rien ne revit`) };
    case 'mois_facture':
      return { ton: 'rouge', texte: morceaux(qui, 'mois déjà facturé : signalez-le à la coordination') };
    case 'annule':
      return { ton: 'vert', texte: morceaux(qui, `scan annulé${plage ? ` (${plage})` : ''}`) };
    case 'garde':
      return { ton: 'orange', texte: morceaux(qui, 'présence gardée : un autre scan l’a constatée') };
    case 'rien_a_annuler':
      return { ton: 'orange', texte: morceaux(qui, 'rien à annuler') };
    case 'invalide':
      return { ton: 'rouge', texte: 'Scan refusé par la base (entrée illisible)' };
    case 'non_traite':
    default:
      return { ton: 'gris', texte: morceaux(qui, 'la base vérifie') };
  }
}
